import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ErrorBoundaryTW } from '../errorBoundary/ErrorBoundaryTW';
import classNames from 'classnames';
import { IHasTestData } from '../../ui-toolkit/test-utilities/IHasTestData';
import { mergeTests } from '../../ui-toolkit/test-utilities/mergeTests';
import type { FCWithChildren } from '../../types/FCWithChildren';

interface SeeMoreLessTWProps extends IHasTestData {
  maxCollapsedHeight?: number;
}

interface SeeMoreLessState {
  isCollapsible: boolean;
  showAll: boolean;
}
export const SeeMoreLessTW: FCWithChildren<SeeMoreLessTWProps> = ({ children, maxCollapsedHeight = 150, ...rest }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isCollapsible, showAll }, setMoreLessState] = useState<SeeMoreLessState>({
    isCollapsible: false,
    showAll: false,
  });

  useLayoutEffect(() => {
    const offsetHeight = ref.current?.offsetHeight ?? 0;
    if (ref.current && offsetHeight > maxCollapsedHeight) {
      setMoreLessState(({ showAll }) => ({ showAll, isCollapsible: true }));
    }
  }, [ref, maxCollapsedHeight]);

  return (
    <ErrorBoundaryTW>
      <div
        ref={ref}
        className={classNames('whitespace-pre-line', {
          'max-h-36 overflow-hidden bg-gradient-to-b from-black to-transparent bg-clip-text text-transparent':
            isCollapsible && !showAll,
        })}
        data-test-id={mergeTests(rest['data-test-id'], 'see-more-less-tw')}
      >
        {children}
      </div>
      <MoreLessButtons seeMoreLessState={{ isCollapsible, showAll }} setMoreLessState={setMoreLessState} />
    </ErrorBoundaryTW>
  );
};

const MoreLessButtons: React.FC<{
  seeMoreLessState: SeeMoreLessState;
  setMoreLessState: React.Dispatch<React.SetStateAction<SeeMoreLessState>>;
}> = ({ seeMoreLessState: { isCollapsible, showAll }, setMoreLessState }) => {
  const handleClick = useCallback(() => {
    setMoreLessState(({ isCollapsible, showAll }) => ({ isCollapsible, showAll: !showAll }));
  }, [setMoreLessState]);
  if (!isCollapsible) {
    return null;
  }
  return (
    <div className={'flex w-full'}>
      <button className={'cursor-pointer pt-4 text-sm font-normal hover:underline'} onClick={handleClick}>
        {`See ${showAll ? 'Less' : 'More'}`}
      </button>
    </div>
  );
};
